import type { InternalRequest } from '../client.js';

// Mirrors server/src/broadcast/audience.service.ts's AudienceSpec — the
// discriminated union the server's resolveAudience() consumes. The client
// only ever sends a SPEC, never a resolved recipient list (see
// broadcast.service.ts's file header: server-side re-resolution is the
// unbypassable security boundary).
export type AudienceSpec =
  | { kind: 'group'; groupIds: number[] }
  | { kind: 'explicit'; userIds: string[] }
  | { kind: 'geofence'; geofenceId: number; maxFixAgeMinutes?: number }
  | { kind: 'radius'; latitude: number; longitude: number; radiusM: number; maxFixAgeMinutes?: number };

export type BroadcastChannel = 'inapp' | 'push' | 'sms' | 'email' | 'telegram' | 'whatsapp';

/** GET /api/broadcasts/status response — booleans only, never credential
 *  values. inapp/push are always true; the four provider channels reflect
 *  whether this instance has credentials configured (server/src/broadcast/
 *  channel-availability.ts is the single source of truth this mirrors). */
export type BroadcastChannelStatus = Record<BroadcastChannel, boolean>;

export interface SmsSegmentCount {
  encoding: 'gsm7' | 'ucs2';
  units: number;
  segments: number;
}

export interface BroadcastChannelEstimate {
  channel: BroadcastChannel;
  reachable: number;
  unreachable: number;
  /** billable units: segments × recipients for SMS, recipients otherwise */
  messages: number;
  /** null when no rate is configured — never 0, which would read as "free" */
  cost: number | null;
}

export interface BroadcastEstimate {
  perChannel: BroadcastChannelEstimate[];
  totalMessages: number;
  paidMessages: number;
  /** null whenever ANY paid channel in use has no configured rate */
  totalCost: number | null;
  segments: SmsSegmentCount;
}

export type BroadcastCapDecision =
  | { ok: true }
  | { ok: false; blocked: 'per_send' | 'monthly'; limit: number; attempted: number };

export interface BroadcastPreviewResponse {
  resolvedCount: number;
  staleExcludedCount: number;
  vehiclesInsideCount: number;
  estimate: BroadcastEstimate;
  cap: BroadcastCapDecision;
  monthUsedPaid: number;
  monthlyLimit: number | null;
}

export interface BroadcastSendResponse {
  id: string;
}

export interface BroadcastRow {
  id: string;
  organization_id: string;
  sender_id: string | null;
  target_type: string;
  target_group_id: number | null;
  body: string;
  broadcast_type: string;
  audience: AudienceSpec | { kind: string } | null;
  channels: BroadcastChannel[] | null;
  confirmed_count: number | null;
  resolved_count: number | null;
  estimate: BroadcastEstimate | null;
  status: 'queued' | 'sending' | 'sent' | 'failed';
  created_at: string;
}

export interface BroadcastListResponse {
  broadcasts: BroadcastRow[];
  nextCursor: string | null;
}

export interface BroadcastReceiptRow {
  id: string;
  user_id: string | null;
  channel: BroadcastChannel;
  endpoint: string | null;
  status: 'queued' | 'sent' | 'failed' | 'skipped';
  reason: string | null;
  provider_ref: string | null;
  segments: number;
  created_at: string;
  completed_at: string | null;
}

export interface BroadcastDetailResponse {
  broadcast: BroadcastRow;
  receipts: BroadcastReceiptRow[];
  nextCursor: string | null;
}

interface PageParams {
  limit?: number;
  before?: string;
}

function qsFrom(params?: PageParams): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  if (params.limit != null) usp.set('limit', String(params.limit));
  if (params.before) usp.set('before', params.before);
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function broadcastsEndpoints(req: InternalRequest) {
  return {
    /** GET /api/broadcasts/status — per-channel deliverability for this
     *  instance, so the composer can disable a channel it cannot actually
     *  send on instead of quoting a real count/cost for one. */
    status: () => req.request<BroadcastChannelStatus>('/broadcasts/status'),

    /** POST /api/broadcasts/preview — resolve + estimate + cap check. Sends
     *  nothing, writes no row. */
    preview: (data: { audience: AudienceSpec; channels: BroadcastChannel[]; body: string }) =>
      req.request<BroadcastPreviewResponse>('/broadcasts/preview', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** POST /api/broadcasts — re-resolves + re-checks caps server-side, then
     *  dispatches asynchronously. Returns 202 with just the new id, NOT the
     *  finished broadcast. */
    send: (data: {
      audience: AudienceSpec;
      channels: BroadcastChannel[];
      body: string;
      confirmedCount: number;
    }) =>
      req.request<BroadcastSendResponse>('/broadcasts', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** GET /api/broadcasts — org-scoped history, cursor-paginated. */
    list: (params?: PageParams) =>
      req.request<BroadcastListResponse>(`/broadcasts${qsFrom(params)}`),

    /** GET /api/broadcasts/:id — detail + cursor-paginated receipts. */
    get: (id: string, params?: PageParams) =>
      req.request<BroadcastDetailResponse>(`/broadcasts/${id}${qsFrom(params)}`),
  };
}
